import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useBranding } from "@/contexts/BrandingContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { CheckCircle, ArrowRight, Sparkle, Star } from "@phosphor-icons/react";

import { devLog } from "@/lib/devLog";
const FEATURE_LABELS = {
  excel_import: "Excel import & export",
  multi_branch: "Multi-branch support",
  custom_branding: "Custom branding",
  api_access: "REST API access",
  priority_support: "Priority support",
};

const TIER_ORDER = ["trial", "starter", "professional", "growth", "enterprise"];

const HIGHLIGHTED = "professional";

const fmtLimit = (n) => (n === -1 ? "Unlimited" : n?.toLocaleString());
const fmtStorage = (n) => (n === -1 ? "Unlimited" : `${n} GB`);

/**
 * Public pricing page. Reads live tiers from `/api/plan/public/pricing` so
 * the prices always match what the super_admin has configured. Trial tier
 * is deliberately rendered as a "Start free" CTA rather than a paid card.
 */
export default function PricingPage() {
  const { brand } = useBranding();
  const navigate = useNavigate();
  const [presets, setPresets] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/plan/public/pricing");
        setPresets(data.presets || {});
      } catch (err) { devLog("pricing fetch failed", err); }
    })();
  }, []);

  const tiers = presets
    ? TIER_ORDER.filter(k => presets[k]).map(k => ({ key: k, ...presets[k] }))
    : [];

  return (
    <div className="min-h-screen bg-background" data-testid="pricing-page">
      {/* Header */}
      <header className="sticky top-0 z-30 bg-background/85 backdrop-blur border-b border-border">
        <div className="max-w-6xl mx-auto flex items-center justify-between px-6 py-4">
          <Link to="/" className="flex items-center gap-2.5">
            <div className="h-9 w-9 rounded-md bg-primary flex items-center justify-center font-display font-bold text-primary-foreground text-lg">
              {(brand.brand_name || "T").charAt(0)}
            </div>
            <div className="font-display font-bold text-lg">{brand.brand_name}</div>
          </Link>
          <div className="flex items-center gap-2">
            <Link to="/login" className="text-sm text-muted-foreground hover:text-foreground px-3 py-1.5">Sign in</Link>
            <Button asChild size="sm" data-testid="pricing-header-cta">
              <Link to="/signup">Start free trial</Link>
            </Button>
          </div>
        </div>
      </header>

      {/* Intro */}
      <section className="max-w-4xl mx-auto px-6 pt-14 pb-8 text-center">
        <div className="overline mb-3 inline-flex items-center gap-1.5 justify-center">
          <Sparkle size={11} weight="duotone" /> Pricing
        </div>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05]">
          Simple, transparent pricing for<br className="hidden sm:block" /> every therapy center.
        </h1>
        <p className="text-muted-foreground text-sm sm:text-base mt-5 max-w-2xl mx-auto">
          Start free for 14 days — no card required. Upgrade when your team is ready.
          Every plan scales with you and keeps every family&apos;s data safely tenant-isolated.
        </p>
      </section>

      {/* Pricing grid */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 pb-16">
        {!presets ? (
          <div className="text-center text-sm text-muted-foreground py-10">Loading pricing…</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            {tiers.map(t => {
              const highlighted = t.key === HIGHLIGHTED;
              const isTrial = t.key === "trial";
              const isEnterprise = t.key === "enterprise";
              const cta = isTrial
                ? { label: "Start free trial", to: "/signup" }
                : isEnterprise
                  ? { label: "Contact sales", to: "mailto:sales@therapyflow.app" }
                  : { label: "Start free trial", to: `/signup?tier=${t.key}` };
              return (
                <Card
                  key={t.key}
                  className={`relative flex flex-col ${highlighted ? "border-primary shadow-lg lg:-mt-3 lg:mb-3" : ""}`}
                  data-testid={`tier-card-${t.key}`}
                >
                  {highlighted && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-2.5 py-0.5 rounded-full bg-primary text-primary-foreground text-[10px] font-semibold flex items-center gap-1">
                      <Star size={10} weight="fill" /> Most popular
                    </div>
                  )}
                  <CardContent className="p-5 flex flex-col flex-1 gap-4">
                    <div>
                      <div className="overline mb-1">{t.tier_name}</div>
                      <div className="flex items-baseline gap-1.5">
                        {isEnterprise ? (
                          <span className="font-display text-3xl font-bold">Custom</span>
                        ) : t.monthly_price > 0 ? (
                          <>
                            <span className="font-display text-3xl font-bold">₹{t.monthly_price.toLocaleString()}</span>
                            <span className="text-xs text-muted-foreground">/ month</span>
                          </>
                        ) : (
                          <span className="font-display text-3xl font-bold">Free</span>
                        )}
                      </div>
                      {isTrial && <p className="text-[11px] text-muted-foreground mt-1">14-day trial. No card.</p>}
                    </div>

                    <div className="space-y-1.5 text-sm">
                      <Line label="Students"  value={fmtLimit(t.student_limit)} />
                      <Line label="Users"     value={fmtLimit(t.user_limit)} />
                      <Line label="Branches"  value={fmtLimit(t.branch_limit)} />
                      <Line label="Storage"   value={fmtStorage(t.storage_gb_limit)} />
                    </div>

                    {t.features && t.features.length > 0 && (
                      <div className="space-y-1.5 pt-1 border-t border-border">
                        {t.features.map(f => (
                          <div key={f} className="flex items-center gap-1.5 text-xs">
                            <CheckCircle size={13} weight="duotone" className="text-primary" />
                            <span>{FEATURE_LABELS[f] || f}</span>
                          </div>
                        ))}
                      </div>
                    )}

                    <div className="flex-1" />
                    <Button
                      asChild={cta.to.startsWith("/")}
                      variant={highlighted ? "default" : "outline"}
                      className="w-full gap-1.5 mt-1"
                      onClick={cta.to.startsWith("mailto") ? () => { window.location.href = cta.to; } : undefined}
                      data-testid={`tier-cta-${t.key}`}
                    >
                      {cta.to.startsWith("/") ? <Link to={cta.to}>{cta.label} <ArrowRight size={13} /></Link> : <>{cta.label} <ArrowRight size={13} /></>}
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </section>

      {/* FAQ / trust strip */}
      <section className="max-w-4xl mx-auto px-6 pb-16">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 text-sm">
          <div>
            <div className="font-display font-semibold mb-1">Change plan anytime</div>
            <p className="text-xs text-muted-foreground">Upgrade or downgrade from your billing settings. Pro-rated automatically.</p>
          </div>
          <div>
            <div className="font-display font-semibold mb-1">Data isolation, always</div>
            <p className="text-xs text-muted-foreground">Every workspace is fully tenant-isolated at the database layer. Nothing leaks between orgs.</p>
          </div>
          <div>
            <div className="font-display font-semibold mb-1">Cancel any time</div>
            <p className="text-xs text-muted-foreground">Your data stays exportable via the Data tab (Excel) even after cancellation.</p>
          </div>
        </div>
      </section>

      <footer className="border-t border-border py-6">
        <div className="max-w-6xl mx-auto px-6 flex items-center justify-between text-xs text-muted-foreground">
          <div>&copy; {new Date().getFullYear()} {brand.brand_name}</div>
          <div className="flex gap-4">
            <Link to="/signup" className="hover:text-foreground">Start free</Link>
            <Link to="/login" className="hover:text-foreground">Sign in</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}


function Line({ label, value }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-muted-foreground text-xs">{label}</span>
      <span className="font-medium tabular-nums">{value}</span>
    </div>
  );
}
